/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric,
    IRequestContext,
    getCustomerAccountNumberForAllChannelType
} from '@msdyn365-commerce/core';
import { CatalogsDataActions, ProductCatalog } from '@msdyn365-commerce/retail-proxy';

import { generateCacheKey } from './utilities';

/**
 * Input for get-catalogs data action.
 */
export class CatalogsInput implements IActionInput {
    public readonly channelId: number;

    public readonly customerAccountNumber?: string;

    private readonly apiSettings: ICommerceApiSettings;

    private readonly locale?: string;

    public constructor(context: IRequestContext, customerAccountNumber?: string) {
        this.channelId = context.apiSettings.channelId ? Number(context.apiSettings.channelId) : 0;
        this.customerAccountNumber = customerAccountNumber ? customerAccountNumber : getCustomerAccountNumberForAllChannelType(context);

        this.apiSettings = context.apiSettings;
        this.locale = context.locale || '';
    }

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string =>
        generateCacheKey(
            this.apiSettings,
            { key: 'CustomerAccountNumber', value: this.customerAccountNumber },
            { key: 'Locale', value: this.locale }
        );

    /**
     * Get Cache object type.
     * @returns - Cache object type.
     */
    public getCacheObjectType = (): string => 'productCatalogs';

    /**
     * Get data cache type.
     * @returns - Cache  type enum.
     */
    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData - The input data passed to the createInput method.
 * @param customerAccountNumber - Customer account number.
 * @returns - Catalogs input.
 */
export const createCatalogsInput = (inputData: ICreateActionContext<IGeneric<IAny>>, customerAccountNumber?: string): IActionInput => {
    if (!inputData.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get product catalog. User is not authenticated.');
    }
    return new CatalogsInput(inputData.requestContext, customerAccountNumber);
};

/**
 * Calls the Retail API and returns the product catalog for an user.
 * @param input - Catalogs Input.
 * @param context - Current context.
 * @returns - Product catalog for the user.
 */
export async function getCatalogAction(input: CatalogsInput, context: IActionContext): Promise<ProductCatalog[]> {
    const productCatalogs = await CatalogsDataActions.getCatalogsAsync(
        { callerContext: context, bypassCache: 'getAndPut' },
        input.channelId,
        true
    );
    return productCatalogs;
}

/**
 * The GetProductCatalogs Data Action
 * Returns product catalogs.
 */
export const getCatalogActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-catalogs',
    action: getCatalogAction as IAction<ProductCatalog[]>,
    input: createCatalogsInput
});

export default getCatalogActionDataAction;
